import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft,
  Edit,
  UserPlus,
  Clock,
  MessageSquare,
  History,
  Paperclip,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Play,
  RefreshCw,
  Send,
  Timer,
  User,
  Tag,
  Package,
  Flag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn, formatDateTime, getStatusColor, getPriorityColor } from '@/lib/utils';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

// SLA Countdown Component
function SLACountdown({ ticket }: { ticket: any }) {
  const now = new Date();
  const slaDue = ticket.slaResolutionDue ? new Date(ticket.slaResolutionDue) : null;
  const firstResponseDue = ticket.slaFirstResponseDue ? new Date(ticket.slaFirstResponseDue) : null;
  
  if (ticket.status === 'CLOSED' || ticket.status === 'RESOLVED' || ticket.status === 'CANCELLED') {
    return (
      <div className="flex items-center gap-2 text-green-600">
        <CheckCircle2 className="w-5 h-5" />
        <span className="font-medium">Ticket Completed</span>
      </div>
    );
  }

  const getTimeRemaining = (due: Date) => {
    const diff = due.getTime() - now.getTime();
    const hours = Math.floor(Math.abs(diff) / (1000 * 60 * 60));
    const minutes = Math.floor((Math.abs(diff) % (1000 * 60 * 60)) / (1000 * 60));
    return { diff, hours, minutes, isOverdue: diff < 0 };
  };

  const resolutionTime = slaDue ? getTimeRemaining(slaDue) : null;
  const responseTime = firstResponseDue && !ticket.firstResponseAt ? getTimeRemaining(firstResponseDue) : null;

  return (
    <div className="space-y-3">
      {responseTime && (
        <div className={cn(
          "flex items-center justify-between p-3 rounded-lg",
          responseTime.isOverdue ? "bg-red-50 border border-red-200" : 
          responseTime.diff < 30 * 60 * 1000 ? "bg-orange-50 border border-orange-200" : 
          "bg-green-50 border border-green-200"
        )}>
          <div className="flex items-center gap-2">
            <Clock className={cn("w-4 h-4", responseTime.isOverdue ? "text-red-600" : "text-muted-foreground")} />
            <span className="text-sm font-medium">First Response</span>
          </div>
          <span className={cn(
            "font-mono font-bold",
            responseTime.isOverdue ? "text-red-600" : 
            responseTime.diff < 30 * 60 * 1000 ? "text-orange-600" : "text-green-600"
          )}>
            {responseTime.isOverdue ? '-' : ''}{responseTime.hours}h {responseTime.minutes}m
          </span>
        </div>
      )}
      {ticket.firstResponseAt && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="text-sm font-medium">First Response</span>
          </div>
          <span className="text-sm text-green-600">Completed</span>
        </div>
      )}
      {resolutionTime && (
        <div className={cn(
          "flex items-center justify-between p-3 rounded-lg",
          resolutionTime.isOverdue ? "bg-red-50 border border-red-200" : 
          resolutionTime.diff < 2 * 60 * 60 * 1000 ? "bg-orange-50 border border-orange-200" : 
          "bg-green-50 border border-green-200"
        )}>
          <div className="flex items-center gap-2">
            <Timer className={cn("w-4 h-4", resolutionTime.isOverdue ? "text-red-600" : "text-muted-foreground")} />
            <span className="text-sm font-medium">Resolution Due</span>
          </div>
          <span className={cn(
            "font-mono font-bold",
            resolutionTime.isOverdue ? "text-red-600" : 
            resolutionTime.diff < 2 * 60 * 60 * 1000 ? "text-orange-600" : "text-green-600"
          )}>
            {resolutionTime.isOverdue ? 'OVERDUE by ' : ''}{resolutionTime.hours}h {resolutionTime.minutes}m
          </span>
        </div>
      )}
    </div>
  );
}

// Comments Section Component
function CommentsSection({ ticketId, comments, onCommentAdded }: { 
  ticketId: string; 
  comments: any[]; 
  onCommentAdded: () => void;
}) {
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const queryClient = useQueryClient();

  const addCommentMutation = useMutation({
    mutationFn: async (data: { content: string; isInternal: boolean }) => {
      const response = await api.post(`/tickets/${ticketId}/comment`, data);
      return response.data;
    },
    onSuccess: () => {
      setNewComment('');
      setIsInternal(false);
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      onCommentAdded();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    addCommentMutation.mutate({ content: newComment, isInternal });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          Comments ({comments.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add Comment Form */}
        <form onSubmit={handleSubmit} className="space-y-3">
          <textarea
            className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
          />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
                className="rounded border-input"
              />
              Internal note (not visible to requester)
            </label>
            <Button 
              type="submit" 
              disabled={!newComment.trim() || addCommentMutation.isPending}
              loading={addCommentMutation.isPending}
            >
              <Send className="w-4 h-4 mr-2" />
              Add Comment
            </Button>
          </div>
        </form>

        {/* Comments List */}
        <div className="space-y-4 pt-4 border-t">
          {comments.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No comments yet</p>
          ) : (
            comments.map((comment: any) => (
              <div 
                key={comment.id} 
                className={cn(
                  "p-4 rounded-lg border",
                  comment.isInternal ? "bg-yellow-50 border-yellow-200" : "bg-muted/30"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                    <span className="text-xs font-medium text-primary">
                      {comment.author?.firstName?.charAt(0)}{comment.author?.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">
                        {comment.author?.firstName} {comment.author?.lastName}
                      </span>
                      {comment.isInternal && (
                        <span className="px-2 py-0.5 text-xs bg-yellow-200 text-yellow-800 rounded">
                          Internal
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(comment.createdAt)}
                      </span>
                    </div>
                    <p className="mt-2 text-sm whitespace-pre-wrap">{comment.content}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Time Entries Section Component
function TimeEntriesSection({ ticketId, timeEntries, onTimeLogged }: { 
  ticketId: string; 
  timeEntries: any[];
  onTimeLogged: () => void;
}) {
  const [minutes, setMinutes] = useState('');
  const [description, setDescription] = useState('');
  const [showForm, setShowForm] = useState(false);
  const queryClient = useQueryClient();

  const logTimeMutation = useMutation({
    mutationFn: async (data: { minutes: number; description?: string }) => {
      const response = await api.post(`/tickets/${ticketId}/time`, data);
      return response.data;
    },
    onSuccess: () => {
      setMinutes('');
      setDescription('');
      setShowForm(false);
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      onTimeLogged();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const mins = parseInt(minutes);
    if (isNaN(mins) || mins <= 0) return;
    logTimeMutation.mutate({ minutes: mins, description: description || undefined });
  };

  const totalMinutes = timeEntries.reduce((sum: number, entry: any) => sum + (entry.durationMinutes || 0), 0);
  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Timer className="w-5 h-5" />
              Time Entries
            </CardTitle>
            <CardDescription>
              Total: {totalHours}h {remainingMinutes}m
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowForm(!showForm)}>
            <Clock className="w-4 h-4 mr-2" />
            Log Time
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showForm && (
          <form onSubmit={handleSubmit} className="p-4 border rounded-lg bg-muted/30 space-y-3">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Minutes Worked</Label>
                <Input
                  type="number"
                  min="1"
                  placeholder="e.g., 30"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Description (Optional)</Label>
                <Input
                  placeholder="What did you work on?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={!minutes || logTimeMutation.isPending}
                loading={logTimeMutation.isPending}
              >
                Log Time
              </Button>
            </div>
          </form>
        )}

        {timeEntries.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">No time entries logged</p>
        ) : (
          <div className="space-y-2">
            {timeEntries.map((entry: any) => (
              <div key={entry.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="text-sm font-medium">
                    {Math.floor(entry.durationMinutes / 60)}h {entry.durationMinutes % 60}m
                  </p>
                  {entry.description && (
                    <p className="text-sm text-muted-foreground">{entry.description}</p>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">
                  {formatDateTime(entry.createdAt)}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// History Timeline Component
function HistoryTimeline({ history }: { history: any[] }) {
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'CREATED': return <Play className="w-4 h-4 text-green-600" />;
      case 'STATUS_CHANGED': return <RefreshCw className="w-4 h-4 text-blue-600" />;
      case 'PRIORITY_CHANGED': return <Flag className="w-4 h-4 text-orange-600" />;
      case 'ASSIGNED': return <UserPlus className="w-4 h-4 text-purple-600" />;
      case 'COMMENT_ADDED': return <MessageSquare className="w-4 h-4 text-gray-600" />;
      case 'INTERNAL_NOTE_ADDED': return <MessageSquare className="w-4 h-4 text-yellow-600" />;
      case 'RESOLVED': return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'CLOSED': return <XCircle className="w-4 h-4 text-gray-600" />;
      case 'REOPENED': return <RefreshCw className="w-4 h-4 text-orange-600" />;
      default: return <History className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActionText = (entry: any) => {
    switch (entry.action) {
      case 'CREATED': return 'Ticket created';
      case 'STATUS_CHANGED': return `Status changed from ${entry.oldValue} to ${entry.newValue}`;
      case 'PRIORITY_CHANGED': return `Priority changed from ${entry.oldValue} to ${entry.newValue}`;
      case 'ASSIGNED': return 'Ticket assigned';
      case 'COMMENT_ADDED': return 'Comment added';
      case 'INTERNAL_NOTE_ADDED': return 'Internal note added';
      case 'RESOLVED': return `Ticket resolved (${entry.newValue})`;
      case 'CLOSED': return 'Ticket closed';
      case 'REOPENED': return 'Ticket reopened';
      default: return entry.action.replace(/_/g, ' ');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="w-5 h-5" />
          History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {history.length === 0 ? (
          <p className="text-center text-muted-foreground py-4">No history yet</p>
        ) : (
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
            <div className="space-y-4">
              {history.map((entry: any, index: number) => (
                <div key={entry.id || index} className="relative flex gap-4 pl-10">
                  <div className="absolute left-0 w-8 h-8 rounded-full bg-background border flex items-center justify-center">
                    {getActionIcon(entry.action)}
                  </div>
                  <div className="flex-1 pb-4">
                    <p className="text-sm font-medium">{getActionText(entry)}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(entry.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function TicketDetailPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [showRequesterModal, setShowRequesterModal] = useState(false);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [resolutionType, setResolutionType] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [requesterEmployeeId, setRequesterEmployeeId] = useState('');

  const { data: ticket, isLoading, error, refetch } = useQuery({
    queryKey: ['ticket', id],
    queryFn: async () => {
      const response = await api.get(`/tickets/${id}`);
      return response.data.data;
    },
  });

  // Fetch users for assignment (all IT staff who can be assigned tickets)
  const { data: usersData } = useQuery({
    queryKey: ['assignableUsers'],
    queryFn: async () => {
      const response = await api.get('/users?limit=100');
      return response.data.data;
    },
  });

  // Mutations with error handlers
  const assignMutation = useMutation({
    mutationFn: async (assigneeId: string) => {
      const response = await api.post(`/tickets/${id}/assign`, { assigneeId });
      return response.data;
    },
    onSuccess: () => {
      setShowAssignModal(false);
      setSelectedAssignee('');
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast({ title: 'Success', description: 'Ticket assigned successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to assign ticket', variant: 'destructive' });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async (status: string) => {
      const response = await api.patch(`/tickets/${id}`, { status });
      return response.data;
    },
    onSuccess: () => {
      setShowStatusModal(false);
      setSelectedStatus('');
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast({ title: 'Success', description: 'Status updated successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to update status', variant: 'destructive' });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: async (data: { resolutionType: string; resolutionNotes?: string }) => {
      const response = await api.post(`/tickets/${id}/resolve`, data);
      return response.data;
    },
    onSuccess: () => {
      setShowResolveModal(false);
      setResolutionType('');
      setResolutionNotes('');
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast({ title: 'Success', description: 'Ticket resolved successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to resolve ticket', variant: 'destructive' });
    },
  });

  const closeMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/tickets/${id}/close`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast({ title: 'Success', description: 'Ticket closed successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to close ticket', variant: 'destructive' });
    },
  });

  const reopenMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/tickets/${id}/reopen`, { reason: 'Reopened by user' });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast({ title: 'Success', description: 'Ticket reopened successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to reopen ticket', variant: 'destructive' });
    },
  });

  const setRequesterMutation = useMutation({
    mutationFn: async (employeeId: string) => {
      const response = await api.patch(`/tickets/${id}/requester`, { employeeId });
      return response.data;
    },
    onSuccess: () => {
      setShowRequesterModal(false);
      setRequesterEmployeeId('');
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast({ title: 'Success', description: 'Requester set successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to set requester', variant: 'destructive' });
    },
  });

  const users = usersData || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <p className="text-lg font-medium">Error loading ticket</p>
        <p className="text-muted-foreground mb-4">There was a problem loading this ticket.</p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-lg font-medium">Ticket not found</p>
        <p className="text-muted-foreground mb-4">The ticket you're looking for doesn't exist or has been deleted.</p>
        <Button asChild>
          <Link to="/tickets">Back to Tickets</Link>
        </Button>
      </div>
    );
  }

  const canResolve = ['OPEN', 'IN_PROGRESS', 'PENDING', 'ON_HOLD'].includes(ticket.status);
  const canClose = ticket.status === 'RESOLVED';
  const canReopen = ['CLOSED', 'RESOLVED'].includes(ticket.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/tickets">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold">{ticket.title}</h1>
              <span className={cn("px-2 py-1 text-xs font-medium rounded-full", getStatusColor(ticket.status))}>
                {ticket.status.replace(/_/g, ' ')}
              </span>
              <span className={cn("px-2 py-1 text-xs font-medium rounded-full", getPriorityColor(ticket.priority))}>
                {ticket.priority}
              </span>
            </div>
            <p className="text-muted-foreground font-mono">{ticket.ticketNumber}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowAssignModal(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            {ticket.assignee ? 'Reassign' : 'Assign'}
          </Button>
          <Button variant="outline" onClick={() => setShowStatusModal(true)}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Change Status
          </Button>
          {canResolve && (
            <Button variant="default" onClick={() => setShowResolveModal(true)}>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Resolve
            </Button>
          )}
          {canClose && (
            <Button 
              variant="secondary" 
              onClick={() => closeMutation.mutate()}
              loading={closeMutation.isPending}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Close
            </Button>
          )}
          {canReopen && (
            <Button 
              variant="outline" 
              onClick={() => reopenMutation.mutate()}
              loading={reopenMutation.isPending}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reopen
            </Button>
          )}
          <Button variant="outline" asChild>
            <Link to={`/tickets/${id}/edit`}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Link>
          </Button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description Card */}
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{ticket.description}</p>
            </CardContent>
          </Card>

          {/* Comments Section */}
          <CommentsSection 
            ticketId={ticket.id} 
            comments={ticket.comments || []} 
            onCommentAdded={refetch}
          />

          {/* Time Entries Section */}
          <TimeEntriesSection 
            ticketId={ticket.id} 
            timeEntries={ticket.timeEntries || []}
            onTimeLogged={refetch}
          />

          {/* History Timeline */}
          <HistoryTimeline history={ticket.history || []} />
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* SLA Status */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                SLA Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SLACountdown ticket={ticket} />
            </CardContent>
          </Card>

          {/* Ticket Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5" />
                Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Category</p>
                <p className="font-medium flex items-center gap-1">
                  {ticket.category?.icon && <span>{ticket.category.icon}</span>}
                  {ticket.category?.name || '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Source</p>
                <p className="font-medium">{ticket.source || 'Portal'}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="font-medium">{formatDateTime(ticket.createdAt)}</p>
              </div>
              {ticket.resolvedAt && (
                <div>
                  <p className="text-sm text-muted-foreground">Resolved</p>
                  <p className="font-medium">{formatDateTime(ticket.resolvedAt)}</p>
                </div>
              )}
              {ticket.closedAt && (
                <div>
                  <p className="text-sm text-muted-foreground">Closed</p>
                  <p className="font-medium">{formatDateTime(ticket.closedAt)}</p>
                </div>
              )}
              {ticket.resolutionType && (
                <div>
                  <p className="text-sm text-muted-foreground">Resolution Type</p>
                  <p className="font-medium">{ticket.resolutionType.replace(/_/g, ' ')}</p>
                </div>
              )}
              {ticket.resolutionNotes && (
                <div>
                  <p className="text-sm text-muted-foreground">Resolution Notes</p>
                  <p className="text-sm">{ticket.resolutionNotes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Requester */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Requester
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ticket.requester ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {ticket.requester.firstName?.charAt(0)}{ticket.requester.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{ticket.requester.firstName} {ticket.requester.lastName}</p>
                    <p className="text-sm text-muted-foreground">{ticket.requester.email}</p>
                    {ticket.requester.phone && (
                      <p className="text-sm text-muted-foreground">{ticket.requester.phone}</p>
                    )}
                    {ticket.requester.department?.name && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {ticket.requester.department.name}
                        {ticket.requester.branch?.name && ` • ${ticket.requester.branch.name}`}
                      </p>
                    )}
                  </div>
                </div>
              ) : ticket.employeeRequester ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-blue-700">
                      {ticket.employeeRequester.firstName?.charAt(0)}{ticket.employeeRequester.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{ticket.employeeRequester.firstName} {ticket.employeeRequester.lastName}</p>
                    <p className="text-xs text-muted-foreground">Employee ID: {ticket.employeeRequester.employeeId}</p>
                    <p className="text-sm text-muted-foreground">{ticket.employeeRequester.email}</p>
                    {ticket.employeeRequester.phone && (
                      <p className="text-sm text-muted-foreground">{ticket.employeeRequester.phone}</p>
                    )}
                    {ticket.employeeRequester.position && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {ticket.employeeRequester.position}
                      </p>
                    )}
                    {ticket.employeeRequester.department?.name && (
                      <p className="text-xs text-muted-foreground">
                        {ticket.employeeRequester.department.name}
                        {ticket.employeeRequester.branch?.name && ` • ${ticket.employeeRequester.branch.name}`}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-muted-foreground">No requester information</p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowRequesterModal(true)}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Set Requester
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assignee */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Assigned To
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ticket.assignee ? (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <span className="text-sm font-medium text-green-700">
                      {ticket.assignee.firstName?.charAt(0)}{ticket.assignee.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">{ticket.assignee.firstName} {ticket.assignee.lastName}</p>
                    <p className="text-sm text-muted-foreground">{ticket.assignee.email}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-2">Unassigned</p>
                  <Button variant="outline" size="sm" onClick={() => setShowAssignModal(true)}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Assign Ticket
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Related Asset */}
          {ticket.asset && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Related Asset
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Link 
                  to={`/assets/${ticket.asset.id}`}
                  className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <Package className="w-8 h-8 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{ticket.asset.name}</p>
                    <p className="text-sm text-muted-foreground font-mono">{ticket.asset.assetTag}</p>
                    {ticket.asset.serialNumber && (
                      <p className="text-xs text-muted-foreground">SN: {ticket.asset.serialNumber}</p>
                    )}
                  </div>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Attachments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                Attachments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ticket.attachments && ticket.attachments.length > 0 ? (
                <div className="space-y-2">
                  {ticket.attachments.map((attachment: any) => {
                    // Build full URL for attachment
                    const baseUrl = import.meta.env.VITE_API_URL ? import.meta.env.VITE_API_URL.replace(/\/api$/, '') : '';
                    const attachmentUrl = attachment.url || `${baseUrl}${attachment.path}`;
                    const displayName = attachment.filename || attachment.originalName || 'Attachment';
                    
                    return (
                      <a
                        key={attachment.id}
                        href={attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 border rounded hover:bg-muted/50 transition-colors"
                      >
                        <Paperclip className="w-4 h-4" />
                        <span className="text-sm truncate">{displayName}</span>
                      </a>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">No attachments</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Assign Ticket</CardTitle>
              <CardDescription>Select a technician to assign this ticket to</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={selectedAssignee}
                onChange={(e) => setSelectedAssignee(e.target.value)}
              >
                <option value="">Select Assignee</option>
                {users.map((user: any) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </option>
                ))}
              </select>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setShowAssignModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => assignMutation.mutate(selectedAssignee)}
                  disabled={!selectedAssignee || assignMutation.isPending}
                  loading={assignMutation.isPending}
                >
                  Assign
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Change Status</CardTitle>
              <CardDescription>Update the ticket status</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">Select Status</option>
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="PENDING">Pending</option>
                <option value="ON_HOLD">On Hold</option>
              </select>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setShowStatusModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => statusMutation.mutate(selectedStatus)}
                  disabled={!selectedStatus || statusMutation.isPending}
                  loading={statusMutation.isPending}
                >
                  Update Status
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Resolve Ticket</CardTitle>
              <CardDescription>Mark this ticket as resolved</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Resolution Type</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={resolutionType}
                  onChange={(e) => setResolutionType(e.target.value)}
                >
                  <option value="">Select Resolution Type</option>
                  <option value="FIXED">Fixed</option>
                  <option value="WORKAROUND">Workaround</option>
                  <option value="DUPLICATE">Duplicate</option>
                  <option value="WONT_FIX">Won't Fix</option>
                  <option value="CANNOT_REPRODUCE">Cannot Reproduce</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Resolution Notes (Optional)</Label>
                <textarea
                  className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-none"
                  placeholder="Describe how the issue was resolved..."
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setShowResolveModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => resolveMutation.mutate({ 
                    resolutionType, 
                    resolutionNotes: resolutionNotes || undefined 
                  })}
                  disabled={!resolutionType || resolveMutation.isPending}
                  loading={resolveMutation.isPending}
                >
                  Resolve Ticket
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Set Requester Modal */}
      {showRequesterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Set Requester</CardTitle>
              <CardDescription>Enter the Employee ID of the person who submitted this ticket</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Employee ID</Label>
                <Input
                  placeholder="e.g., ugsl0206"
                  value={requesterEmployeeId}
                  onChange={(e) => setRequesterEmployeeId(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  The Employee ID can be found on the staff badge or HR records
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => { setShowRequesterModal(false); setRequesterEmployeeId(''); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => setRequesterMutation.mutate(requesterEmployeeId)}
                  disabled={!requesterEmployeeId.trim() || setRequesterMutation.isPending}
                  loading={setRequesterMutation.isPending}
                >
                  Set Requester
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
